export const classNames = (...classes) => classes.filter(Boolean).join(' ');

/** 'FY2526' → 2025 (the calendar year the financial year starts in). */
const fyStartYear = (fy) => {
  const match = /^FY(\d{2})/.exec(fy || '');
  return match ? 2000 + parseInt(match[1], 10) : 2025;
};

/**
 * Order objectives by their code number (O1, O2, … O13), so O13 sorts after O2
 * rather than before it as a plain string compare would. Objectives with no
 * usable code fall to the end, alphabetical by title.
 */
export const compareObjectiveCodes = (a, b) => {
  const num = (obj) => {
    const match = /^O(\d+)$/i.exec((obj?.code || '').trim());
    return match ? parseInt(match[1], 10) : Number.POSITIVE_INFINITY;
  };
  const diff = num(a) - num(b);
  if (diff !== 0 && Number.isFinite(diff)) return diff;
  if (num(a) !== num(b)) return num(a) === Number.POSITIVE_INFINITY ? 1 : -1;
  return (a?.objective || '').localeCompare(b?.objective || '');
};

/**
 * Does `userId` own an individual KR anywhere under `kr`? Checks both the
 * FY26-27 team-KR layer and the legacy flat layer, because the same collection
 * holds documents in both shapes.
 */
export const krHasIkrOwner = (kr, userId) => {
  if (!userId) return false;
  return (kr?.teamKRs || []).some(tkr => (tkr?.individualKRs || []).some(i => i?.ownerId === userId))
    || (kr?.individualKRs || []).some(i => i?.ownerId === userId);
};

/**
 * Every FY26-27 key-result code that must never be issued again: the live code
 * of every key result, plus every code still sitting in someone's
 * `accessibleKRs`.
 *
 * The second half is the important one. A code held but no longer live belonged
 * to a deleted key result, and giving it to a new one would silently grant that
 * new KR to whoever still holds it — so those codes stay reserved even though
 * nothing points at them any more.
 */
export const reservedKrCodes = (okrs, users) => {
  const codes = new Set();
  const add = (value) => {
    const code = String(value || '').trim();
    if (code) codes.add(code);
  };
  (okrs || []).forEach(obj => (obj?.keyResults || []).forEach(kr => add(kr?.code)));
  (users || []).forEach(user => {
    if (Array.isArray(user?.accessibleKRs)) user.accessibleKRs.forEach(add);
  });
  return codes;
};

/**
 * The next free key-result code inside one objective — `O12.K2.4` where the
 * objective's highest group is K2 and its highest sequence in that group is 3.
 * Continues the last group rather than opening a new one, so codes stay
 * compact; the sequence steps over anything in `reservedCodes` instead of
 * filling a gap left by a deleted key result.
 *
 * A key result with no code is invisible to every non-Admin —
 * `canUserAccessObjectiveFY2627` and ObjectiveCardFY2627 both match on the
 * code, and a blank one can never appear in `accessibleKRs`. So every new key
 * result has to be born with a code, which is what this exists to supply.
 *
 * @param   {Object} objective      the parent objective, whose `code` seeds the result
 * @param   {Set}    reservedCodes  from reservedKrCodes()
 * @returns {?string} null when the objective's own code is missing or malformed,
 *          leaving the caller to ask for one rather than invent a code
 */
export const nextKrCode = (objective, reservedCodes = new Set()) => {
  const objectiveCode = String(objective?.code || '').trim();
  if (!/^O\d+$/i.test(objectiveCode)) return null;

  const parse = (code) => {
    const match = /^O\d+\.K(\d+)\.(\d+)$/i.exec(String(code || '').trim());
    return match ? { group: parseInt(match[1], 10), seq: parseInt(match[2], 10) } : null;
  };

  const own = (objective?.keyResults || []).map(kr => parse(kr?.code)).filter(Boolean);
  // An objective whose key results are all uncoded starts the K1 sequence.
  const group = own.reduce((max, p) => Math.max(max, p.group), 0) || 1;
  const highestSeq = own
    .filter(p => p.group === group)
    .reduce((max, p) => Math.max(max, p.seq), 0);

  // Terminates: reservedCodes is finite, so the sequence outruns it.
  let seq = highestSeq + 1;
  while (reservedCodes.has(`${objectiveCode}.K${group}.${seq}`)) seq++;
  return `${objectiveCode}.K${group}.${seq}`;
};

/** Financial years the app has data and views for, oldest first. */
export const SUPPORTED_FYS = ['FY2526', 'FY2627'];

/**
 * The financial year a date falls in (FY runs 1 April – 31 March), clamped to
 * the years the app supports so a date outside that range still lands on a
 * usable view rather than an empty one.
 */
export const getCurrentFinancialYear = (date = new Date()) => {
  const y = date.getFullYear();
  const startYear = date.getMonth() + 1 >= 4 ? y : y - 1;   // Jan–Mar belongs to the previous FY
  const code = `FY${String(startYear % 100).padStart(2, '0')}${String((startYear + 1) % 100).padStart(2, '0')}`;

  if (SUPPORTED_FYS.includes(code)) return code;
  return startYear < fyStartYear(SUPPORTED_FYS[0])
    ? SUPPORTED_FYS[0]
    : SUPPORTED_FYS[SUPPORTED_FYS.length - 1];
};

/**
 * Which quarter of the given financial year a date falls in.
 * FY runs Apr–Mar: Q1 Apr–Jun, Q2 Jul–Sep, Q3 Oct–Dec, Q4 Jan–Mar.
 *
 * @returns {'Q1'|'Q2'|'Q3'|'Q4'|null} null when the date is outside the FY
 */
export const getFYQuarter = (fy, date = new Date()) => {
  const start = fyStartYear(fy);
  const y = date.getFullYear();
  const m = date.getMonth() + 1;

  if (y === start && m >= 4 && m <= 6) return 'Q1';
  if (y === start && m >= 7 && m <= 9) return 'Q2';
  if (y === start && m >= 10 && m <= 12) return 'Q3';
  if (y === start + 1 && m >= 1 && m <= 3) return 'Q4';
  return null;
};

/**
 * Default evaluation period for a financial year: the quarter we're currently
 * in, 'Annual' once the FY has ended, and Q1 for an FY that hasn't started.
 */
export const getDefaultEvaluationPeriod = (fy, date = new Date()) => {
  const current = getFYQuarter(fy, date);
  if (current) return current;

  const start = fyStartYear(fy);
  const fyStart = new Date(start, 3, 1);        // 1 April
  return date < fyStart ? 'Q1' : 'Annual';
};

export const normalizeQuarterlyActuals = (actuals) => {
  if (!actuals || !Array.isArray(actuals)) return [null, null, null, null];
  const padded = actuals.concat([null, null, null, null]).slice(0, 4);
  if (padded.every(val => val === 0)) return [null, null, null, null];
  return padded.map(val => (val === undefined ? null : val));
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
};

const normalizeQuarterObjects = (ikr) => {
  if (!Array.isArray(ikr?.quarters)) return null;
  const quarterCodes = ['Q1', 'Q2', 'Q3', 'Q4'];
  return quarterCodes.map((code, i) => {
    const q = ikr.quarters[i] || {};
    return {
      q: q.q || code,
      text: q.text || '',
      target: toNumberOrNull(q.target),
      actual: toNumberOrNull(q.actual)
    };
  });
};

export const calculateIkrProgress = (ikr) => {
  const method = ikr?.calculationMethod || 'sum';
  const quarterObjects = normalizeQuarterObjects(ikr);
  const yearlyTarget = toNumberOrNull(ikr?.yearlyTarget);

  if (quarterObjects) {
    const targets = quarterObjects.map(q => q.target);
    const actuals = quarterObjects.map(q => q.actual);

    // If yearlyTarget is provided, treat it as the primary denominator.
    if (yearlyTarget !== null && yearlyTarget > 0) {
      if (method === 'average') {
        const filledActuals = actuals.filter(a => a !== null);
        if (filledActuals.length === 0) return 0;
        const avgActual = filledActuals.reduce((sum, a) => sum + a, 0) / filledActuals.length;
        return avgActual / yearlyTarget;
      }

      if (method === 'deviation') {
        for (let i = actuals.length - 1; i >= 0; i--) {
          const a = actuals[i];
          if (a !== null) {
            if (a === 0) return 0;
            return yearlyTarget / a;
          }
        }
        return 0;
      }

      const totalActual = actuals.reduce((sum, a) => sum + (a || 0), 0);
      return totalActual / yearlyTarget;
    }

    if (method === 'average') {
      const filled = quarterObjects.filter(q => q.actual !== null && q.target !== null && q.target !== 0);
      if (filled.length === 0) return 0;
      const targetAvg = filled.reduce((sum, q) => sum + q.target, 0) / filled.length;
      if (targetAvg <= 0) return 0;
      const actualAvg = filled.reduce((sum, q) => sum + q.actual, 0) / filled.length;
      return actualAvg / targetAvg;
    }

    if (method === 'deviation') {
      for (let i = quarterObjects.length - 1; i >= 0; i--) {
        const t = targets[i];
        const a = actuals[i];
        if (t !== null && t !== 0 && a !== null) {
          if (a === 0) return 0;
          return t / a;
        }
      }
      return 0;
    }

    const totalTarget = targets.reduce((sum, t) => sum + (t || 0), 0);
    if (totalTarget <= 0) return 0;
    const totalActual = actuals.reduce((sum, a) => sum + (a || 0), 0);
    return totalActual / totalTarget;
  }

  const actuals = normalizeQuarterlyActuals(ikr?.quarterlyActuals);
  const target = parseFloat(ikr?.kpiTarget || ikr?.targetKpi) || 0;

  if (target <= 0) return 0;

  if (method === 'average') {
    const filledActuals = actuals.filter(val => val !== null && val !== undefined && val !== '');
    if (filledActuals.length === 0) return 0;
    const avg = filledActuals.reduce((sum, val) => sum + (parseFloat(val) || 0), 0) / filledActuals.length;
    return avg / target;
  } else if (method === 'deviation') {
    let latestActual = null;
    for (let i = actuals.length - 1; i >= 0; i--) {
      if (actuals[i] !== null && actuals[i] !== undefined && actuals[i] !== '') {
        latestActual = parseFloat(actuals[i]) || 0;
        break;
      }
    }
    if (latestActual === null) return 0;
    if (latestActual === 0) return target > 0 ? 2 : 0;
    return target / latestActual;
  } else {
    const total = actuals.reduce((sum, val) => sum + (val != null ? (parseFloat(val) || 0) : 0), 0);
    return total / target;
  }
};

/** Text shown where a timestamp should be but none was ever recorded. */
export const NO_TIMESTAMP_LABEL = 'Submitted date not recorded';

/**
 * Render an evaluation timestamp for display.
 *
 * Handles the three things that turn up in these fields: a Firestore Timestamp
 * (what `selfEvaluationSubmittedAt` writes), an ISO string (what the older
 * `lastUpdated` fields hold), and nothing at all. Evaluations submitted before
 * the timestamp existed fall into the last case — they are labelled as not
 * recorded rather than guessed at from a field the manager's edits overwrite.
 *
 * @param {*} value  Firestore Timestamp | ISO string | Date | null
 * @returns {string} a formatted date-time, or NO_TIMESTAMP_LABEL
 */
export const formatSubmittedAt = (value) => {
  const date = toDateOrNull(value);
  if (!date) return NO_TIMESTAMP_LABEL;
  return date.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/** Same inputs as formatSubmittedAt, but a Date (or null) rather than text. */
export const toDateOrNull = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
